import { Grid, useMediaQuery } from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import DeleteIcon from '@material-ui/icons/Delete';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Spin from 'components/Spin';
import type { MouseEvent } from 'react';
import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable } from 'react-use-observable';
import { tap } from 'rxjs/operators';
import { customerFactory } from 'v2/services/customer';

import type { ICardApiResponse } from 'v2/interfaces/customer';
import type { ISubscriptions } from 'v2/interfaces/subscriptions';

import DialogCardUsage from './dialogCardUsage';
import DialogRemoveCard from './dialogRemoveCard';
import { useStyles } from './styles';
import PreviewCard from '../previewCard';

import type { ICard } from '../index';

interface IProps {
  cardsList: ICardApiResponse[];
  cardUsage?: ISubscriptions[];
  isLoadingCards?: boolean;
  isLoadingCardUsage?: boolean;
  selectedCard?: ICard;
  onCardClick?: (card: ICard) => void;
  getCardUsage?: (cardId: number) => void;
  cleanCardUsage?: () => void;
}

const ListCards = ({ cardsList, cardUsage, isLoadingCardUsage, isLoadingCards, selectedCard, onCardClick, cleanCardUsage, getCardUsage }: IProps) => {
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);
  const [cardId, setCardId] = useState(null);
  const [isDialogCardUsageOpen, setIsDialogCardUsageOpen] = useState(false);
  const [isDialogRemoveCardOpen, setIsDialogRemoveCardOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const isLaptopOrLow = useMediaQuery('@media screen and (max-width: 1230px)');

  const [onRemove] = useCallbackObservable((cardRemoveId) => {
    setIsRemoving(true);

    return customerFactory.deleteCard(cardRemoveId).pipe(
      tap(() => {
        setAnchorEl(null);
        toast.success('Forma de pagamento excluída com sucesso!');
        setIsDialogRemoveCardOpen(false);
        setIsRemoving(false);
      })
    );
  }, []);

  const handleOpenMenu = (event: MouseEvent<HTMLSpanElement>, openCardId: number) => {
    setCardId(openCardId);
    setAnchorEl(event.currentTarget);
  };

  const resetUsesFromCard = useCallback(() => {
    setCardId('');
    cleanCardUsage();
  }, [cleanCardUsage]);

  const handleClose = () => {
    setAnchorEl(null);
    resetUsesFromCard();
  };

  const handleUsageClick = () => {
    setAnchorEl(null);
    setIsDialogCardUsageOpen(true);
    getCardUsage(cardId);
  };

  const handleRemoveCardClick = () => {
    setAnchorEl(null);
    setIsDialogRemoveCardOpen(true);
    getCardUsage(cardId);
  };

  const closeDialogCardUsage = useCallback(() => {
    setIsDialogCardUsageOpen(false);
    resetUsesFromCard();
  }, [resetUsesFromCard]);

  const closeDialogRemoveCard = useCallback(() => {
    setIsDialogRemoveCardOpen(false);
    resetUsesFromCard();
  }, [resetUsesFromCard]);

  if (isLoadingCards) {
    return (
      <div style={{ textAlign: 'center', padding: 16 }}>
        <Spin />
        <Typography variant='body1'>Carregando formas de pagamento cadastradas</Typography>
      </div>
    );
  }

  if (!cardsList?.length) return <Typography variant='body2'>Nenhuma forma de pagamento cadastrada encontrada.</Typography>;

  return (
    <>
      <Grid spacing={isLaptopOrLow ? 1 : 2} container justifyContent={isLaptopOrLow ? 'flex-start' : 'space-between'}>
        {cardsList?.map((card) => {
          const currentCard: ICard = { id: card.id, name: card.holder, number: card.number, cvc: '', expiry: '', issuer: card.flag };
          return (
            <Grid className={`${classes.card} ${onCardClick ? classes.cardSelectable : classes.cardEditable}`} key={card.id} item>
              {!onCardClick && (
                <IconButton className={classes.editIcon} onClick={(event) => handleOpenMenu(event, card.id)}>
                  <MoreHorizIcon />
                </IconButton>
              )}
              <span onClick={onCardClick ? () => onCardClick(currentCard) : () => ({})}>
                {selectedCard?.id === currentCard.id && (
                  <Icon className={classes.icon} color='primary'>
                    check_circle_sharp_icon
                  </Icon>
                )}
                <PreviewCard card={currentCard} />
              </span>
            </Grid>
          );
        })}

        {!onCardClick && (
          <>
            <Menu
              id='card-actions-menu'
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={handleClose}
              elevation={0}
              classes={{ paper: classes.menuPaper }}
            >
              <MenuItem onClick={handleUsageClick}>
                <ListItemIcon className={classes.menuItemIcon}>
                  <VisibilityIcon color='primary' />
                </ListItemIcon>
                <ListItemText>Ver uso</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleRemoveCardClick}>
                <ListItemIcon className={classes.menuItemIcon}>
                  <DeleteIcon color='error' />
                </ListItemIcon>
                <ListItemText classes={{ primary: classes.removeCardText }}>Remover cartão</ListItemText>
              </MenuItem>
            </Menu>

            <DialogCardUsage isOpen={isDialogCardUsageOpen} loading={isLoadingCardUsage} cardUsage={cardUsage} handleClose={closeDialogCardUsage} />
            <DialogRemoveCard
              isOpen={isDialogRemoveCardOpen}
              loading={isLoadingCardUsage}
              removing={isRemoving}
              cardUsage={cardUsage}
              handleClose={closeDialogRemoveCard}
              onRemove={() => onRemove(cardId)}
            />
          </>
        )}
      </Grid>
    </>
  );
};

export default ListCards;
